import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { kaylasApi } from "./kaylasApi";
import { KaylasDrawer, PhotoField, FormSection } from "./KaylasDrawer";
import { Plus, Search, Trash2, Loader2, BookOpen, Sparkles, Edit3 } from "lucide-react";
import { toast } from "sonner";

const empty = { date: "", title: "", body: "", weather: "", highlights: "", lessons: "", photos: [] };

export default function KaylasJournal() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [drawer, setDrawer] = useState(() =>
    new URLSearchParams(window.location.search).get("add") === "1"
  );

  const load = async (q) => { setLoading(true); try { setItems(await kaylasApi.listJournal(q)); } finally { setLoading(false); } };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await kaylasApi.listJournal();
        if (!cancelled) setItems(r);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    if (params.get("add") === "1") {
      params.delete("add");
      setParams(params, { replace: true });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      const q = search.trim();
      kaylasApi.listJournal(q || undefined).then(setItems).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = useMemo(() => {
    if (filter === "auto") return items.filter((i) => i.auto);
    if (filter === "manual") return items.filter((i) => !i.auto);
    return items;
  }, [items, filter]);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this journal entry?")) return;
    await kaylasApi.deleteJournal(id);
    toast.success("Entry removed");
    await load(search.trim() || undefined);
  };

  return (
    <div className="space-y-6" data-testid="mums-journal-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="mum-chip"><BookOpen size={12} /> Tab 7</div>
          <h1 className="font-display text-4xl mt-2">Garden Journal</h1>
          <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">The heart of Kayla&apos;s Garden — a living history.</p>
        </div>
        <button className="mum-btn" onClick={() => setDrawer(true)} data-testid="mums-journal-add"><Plus size={15} /> New Entry</button>
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mum-cobalt)]/60" />
          <input className="mum-field pl-9" placeholder="Search journal…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mums-journal-search" />
        </div>
        <select className="mum-field sm:w-48" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="mums-journal-filter">
          <option value="all">All entries</option>
          <option value="auto">Auto-written</option>
          <option value="manual">By me</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="kaylas-card-quiet p-10 text-center" data-testid="mums-journal-empty">
          <BookOpen size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">Your story begins today.</p>
          <button className="mum-btn mt-5" onClick={() => setDrawer(true)}><Plus size={15} /> Write first entry</button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="mums-journal-list">
          {filtered.map((j) => (
            <article key={j.id} className="kaylas-card p-5" data-testid={`journal-${j.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)] flex items-center gap-2">
                    {prettyDate(j.date)}
                    {j.auto && <span className="mum-chip" title="Automatic entry"><Sparkles size={10} /> auto</span>}
                  </div>
                  {j.title && <h3 className="font-display text-2xl text-[var(--mum-sapphire)] mt-1">{j.title}</h3>}
                </div>
                <button className="mum-btn-ghost" onClick={() => onDelete(j.id)} data-testid={`journal-delete-${j.id}`}><Trash2 size={13} /></button>
              </div>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{j.body}</p>
              {(j.weather || j.highlights || j.lessons) && (
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                  {j.weather && <dd className="bg-[var(--mum-sky)]/40 rounded-lg p-2"><strong className="block">Weather</strong>{j.weather}</dd>}
                  {j.highlights && <dd className="bg-[var(--mum-sky)]/40 rounded-lg p-2"><strong className="block">Highlights</strong>{j.highlights}</dd>}
                  {j.lessons && <dd className="bg-[var(--mum-sky)]/40 rounded-lg p-2"><strong className="block">Lessons</strong>{j.lessons}</dd>}
                </dl>
              )}
              {(j.photos || []).length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
                  {j.photos.map((u, i) => (
                    <div key={i} className="aspect-square overflow-hidden rounded-lg border border-[var(--mum-cornflower)]">
                      <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <KaylasDrawer open={drawer} onClose={() => setDrawer(false)} title="New Journal Entry" testid="mums-journal-drawer">
        <JournalForm onClose={() => setDrawer(false)} onSaved={async () => { setDrawer(false); await load(search.trim() || undefined); }} />
      </KaylasDrawer>
    </div>
  );
}

function JournalForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) { toast.error("Write a few words to remember the day."); return; }
    setBusy(true);
    try {
      await kaylasApi.createJournal(form);
      toast.success("Entry saved");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't save the entry");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="journal-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Date</span>
          <input type="date" className="mum-field mt-1.5" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="journal-date" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Title (optional)</span>
          <input className="mum-field mt-1.5" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="A short title" data-testid="journal-title" />
        </label>
      </div>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">The entry *</span>
        <textarea className="mum-field mt-1.5" rows={6} value={form.body} onChange={(e) => set("body", e.target.value)} required placeholder="Today in the garden…" data-testid="journal-body" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Weather</span>
        <input className="mum-field mt-1.5" value={form.weather} onChange={(e) => set("weather", e.target.value)} placeholder="Crisp · light rain · 14°C" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Highlights</span>
        <input className="mum-field mt-1.5" value={form.highlights} onChange={(e) => set("highlights", e.target.value)} />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Lessons learned</span>
        <input className="mum-field mt-1.5" value={form.lessons} onChange={(e) => set("lessons", e.target.value)} />
      </label>
      <PhotoField label="Photos" value={form.photos} onChange={(v) => set("photos", v)} multi testid="journal-photos" />
      <div className="flex gap-2 sticky bottom-0 bg-[var(--mum-paper)] pt-3 -mx-5 px-5 pb-1 border-t border-[var(--mum-sky)]">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1" disabled={busy} data-testid="journal-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Edit3 size={14} />}
          Save entry
        </button>
      </div>
    </form>
  );
}

function prettyDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "full" }); }
  catch { return iso; }
}
